import { MapboxOverlay as DeckOverlay } from '@deck.gl/mapbox';
import { IFlightItem, LngLatArray } from '../flights/types';
import { IconSize, iconSizeScale } from '../IconSize';
import { DeckOverlayPlugin } from './deck-overlay-plugin';
import { DeckOverlayProps } from './types';

export abstract class DeckLayerCreator {
    protected plugin: DeckOverlayPlugin;

    constructor(plugin: DeckOverlayPlugin) {
        this.plugin = plugin;
        this.getIconScale = this.getIconScale.bind(this);
        this.getIsHidden = this.getIsHidden.bind(this);
        this.isItemSelected = this.isItemSelected.bind(this);
        this.getFlightPosition = this.getFlightPosition.bind(this);
    }

    protected get overlay(): DeckOverlay | null {
        return this.plugin.overlay;
    }

    protected get props(): DeckOverlayProps | null {
        return this.plugin.props;
    }

    protected get propsVersion(): number {
        return this.plugin.propsVersion;
    }

    protected get flightsVersion(): number {
        return this.plugin.flightsVersion;
    }

    protected get selectionVersion(): number {
        return this.plugin.selection.version;
    }

    protected get isCoastMode(): boolean {
        return !!this.props?.flights.active.isCoastMode;
    }

    protected get zoom(): number {
        return this.plugin.map?.getZoom() ?? 0;
    }

    protected get useGlobeView(): boolean {
        return !!this.props?.globeView.useGlobeView;
    }

    protected get getPositionUpdateTrigger() {
        return [this.propsVersion];
    }

    protected get getIsHiddenUpdateTrigger() {
        return [this.propsVersion, this.selectionVersion];
    }

    protected get autoArrangeVersion() {
        return this.plugin.autoArrange.version;
    }

    protected getIconScale() {
        const { iconSize = IconSize.medium, iconSizeZoomThreshold = 0 } = this.props?.flights.active || {};
        let currentSize = iconSize;
        if (iconSize === IconSize.auto) {
            currentSize = this.zoom <= iconSizeZoomThreshold ? IconSize.small : IconSize.medium;
        }
        return iconSizeScale(currentSize);
    }

    protected getIsHidden(flight: IFlightItem) {
        const { showOnlySelected, visible: activeVisible } = this.props!.flights.active;
        const groundVehiclesVisible = this.props!.flights.groundVehicles.visible;
        if (flight == null) {
            return 1;
        }

        return showOnlySelected && (this.plugin.selectedFlights?.length ?? 0 > 0)
            ? this.isItemSelected(flight)
                ? 0
                : 1
            : (activeVisible && flight.isAircraft) || (groundVehiclesVisible && flight.isGroundVehicle)
              ? 0
              : 1;
    }

    protected getFlightPosition(item: IFlightItem): LngLatArray {
        // If we don't have a last real position for this aircraft use regular position report so that we display it at all
        return (this.props?.flights.active.showProjectedPoints ? item.position : item.lastRealReport?.position) ?? item.position;
    }

    protected getFlightSpeed(item: IFlightItem): number {
        return (this.props?.flights.active.showProjectedPoints ? item.speed : item.lastRealReport?.speed) ?? 0;
    }

    protected getFlightAltitude(item: IFlightItem): number | null {
        return this.props?.flights.active.showProjectedPoints ? item.altitude : (item.lastRealReport?.altitude ?? null);
    }

    protected getFlightHeading(item: IFlightItem): number | null {
        return this.props?.flights.active.showProjectedPoints ? item.heading : (item.lastRealReport?.heading ?? null);
    }

    protected getFlightLastReportTime(item: IFlightItem): number {
        return (this.props?.flights.active.showProjectedPoints ? item.positionReportTime : item.lastRealReport?.positionReportTime) ?? 0;
    }

    protected getFlightFeedId(item: IFlightItem): number | null {
        return (this.props?.flights.active.showProjectedPoints ? item.feedId : item.lastRealReport?.feedId) ?? null;
    }

    private isItemSelected(flight: IFlightItem): boolean {
        return this.plugin.selection.isSelected(flight.id);
    }
}
